import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Smartphone, Tablet, Monitor, Zap, Users, TrendingUp } from 'lucide-react';

const AppDevelopment = () => {
  const capabilities = [
    {
      icon: <Smartphone className="h-6 w-6" />,
      title: "Native iOS & Android Apps",
      description: "Platform-specific apps that leverage native features and performance"
    },
    {
      icon: <Monitor className="h-6 w-6" />,
      title: "Cross-Platform Development",
      description: "React Native and Flutter apps that work across multiple platforms"
    },
    {
      icon: <Tablet className="h-6 w-6" />,
      title: "Responsive Design",
      description: "Beautiful interfaces that adapt to all screen sizes and orientations"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Real-time Features",
      description: "Push notifications, live chat, and real-time data synchronization"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Analytics Integration",
      description: "Track user behavior and app performance with detailed analytics"
    }
  ];

  const whyVincotek = [
    "User-centered design approach with focus on exceptional UX",
    "Full-stack development expertise for complete app ecosystems",
    "App Store optimization and deployment support",
    "Post-launch support with updates and feature enhancements"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-vincotek-yellow to-yellow-500 text-vincotek-dark py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              App Development
            </h1>
            <p className="text-xl md:text-2xl text-gray-800 max-w-3xl mx-auto leading-relaxed">
              Native and cross-platform mobile applications designed to engage users and drive business growth in today's mobile-first world.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8 text-center">
              What We Do
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center leading-relaxed">
              We build mobile applications that connect businesses with their customers. Our team specializes in creating intuitive, high-performance apps that solve real problems and deliver exceptional user experiences across all platforms.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="text-vincotek-blue mb-4">
                      {capability.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                      {capability.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {capability.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              Who This Is For
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Perfect for businesses wanting to reach customers on mobile, entrepreneurs with innovative app ideas, and organizations looking to streamline operations through mobile solutions. We work with retail businesses, service providers, startups, and enterprises to create apps that enhance customer engagement, improve operational efficiency, and drive revenue growth.
            </p>
          </div>
        </div>
      </section>

      {/* Why Vincotek Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Why Choose Vincotek
            </h2>
            <div className="space-y-6">
              {whyVincotek.map((reason, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-vincotek-yellow rounded-full flex items-center justify-center">
                    <span className="text-vincotek-dark font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Mobile App?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's turn your app idea into reality and connect with your customers on mobile.
          </p>
          <Link to="/contact">
            <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AppDevelopment;